import { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import { cx } from '../cx';
import { Icon, type IconName } from '../Icon/Icon';
import { MOTION } from '../motion';

/**
 * A segmented control: two to four mutually exclusive choices, shown together,
 * with a thumb that SLIDES to the chosen one.
 *
 * The sliding thumb is not decoration. A segmented control is a spatial control -
 * the choices sit in a row and the selection has a position - so the selection
 * moving between two positions is the honest animation for it. Cross-fading a
 * highlight would hide the very relationship the control exists to show.
 *
 * How the thumb works, and why it needs no measurement: the segments are a grid of
 * equal columns, so segment `i` starts at exactly `i * 100%` of the thumb's own
 * width. One `translateX` on a token-timed transition - no ResizeObserver, no
 * layout read - which is also why it cannot drift when the font or the container
 * changes size.
 *
 * IT IS A REAL RADIO GROUP. A fieldset, a legend, and one `<input type="radio">`
 * per segment, all sharing a name. That buys the entire keyboard model from the
 * platform (one tab stop, arrows move AND select, wrapping at both ends) instead of
 * re-implementing it on div soup, and it is what a screen reader already knows how
 * to announce. The input is transparent and covers its segment, so the global
 * focus-visible ring lands exactly on the segment outline.
 */
export interface SegmentDef<T extends string> {
  readonly value: T;
  readonly label: string;
  readonly icon?: IconName | undefined;
  /**
   * THE COUNT SLOT (fe-foundation; clients-v2.html:109 and :120-130, "Active 102"). A number
   * after the label in the secondary ink, tabular so a changing count does not shift the
   * label; words and counts only, no symbol inside the control (register L12). Absent draws
   * nothing, so every existing caller renders as before.
   */
  readonly count?: number | undefined;
}

export interface SegmentedControlProps<T extends string> {
  readonly value: T;
  readonly onValueChange: (value: T) => void;
  readonly segments: readonly SegmentDef<T>[];
  /** Required: a group of radios with no name is a group of unexplained circles. */
  readonly ariaLabel: string;
  readonly size?: 'sm' | 'md' | undefined;
  /**
   * WHICH OF APPLE'S SEGMENTED CONTROLS THIS IS (P3 round 2 F4, P5 round 2 F3).
   *
   * `glass` is the TOOLBAR's, and since the shell round it is the kit's toolbar master
   * rather than a capsule with a raised white thumb: `Titlebars and Toolbars/Light/XL/
   * Segmented Control/Active/4 Items/1 Selected` is 36 tall with 4 of padding, its buttons
   * are 32x28, and the SELECTED one is a 7 percent black fill (8 percent white in the
   * dark), not a lifted surface. It stays the default because that is where every existing
   * caller has one.
   *
   * `content` is the Mac's content-area control as HOME takes it (kit: Segmented
   * Controls/Light/Content Area/Duo/Active, 3 Rg - 60x24, radius 6, track #000000 at 8
   * percent) with the kit's accent ON fill deliberately NOT taken: `spec/screens/home.md`
   * section 7 makes "no primary button on the overview screen" an acceptance item, so the
   * thumb stays a surface. Ledger P3-16.
   *
   * `contentTinted` is the SAME kit family with the master's own On layer style (Trio,
   * accent fill, white label). The record's chip row takes it because that screen has no
   * such acceptance item and the fill is what the master paints. Ledger P5-13, and P5-17
   * for why the two live side by side after the merge rather than one of them winning.
   *
   * `ios` is the phone's standard control (kit: Segmented Controls/Light/Small/1 -
   * Enabled): 32 tall, a capsule track filled at 12 percent, 2 of padding, 4 between the
   * items, and a white capsule thumb with a soft shadow.
   */
  readonly variant?: 'glass' | 'content' | 'contentTinted' | 'ios' | undefined;
  /** Drops the labels below the desktop breakpoint, keeping the icons. */
  readonly compactLabels?: boolean | undefined;
  /**
   * HOW WIDE A SEGMENT IS (BR-PARTS; it replaced the Clients page's `FitSegments`, ledger
   * FE-CL-10). `equal`, the default and every existing caller's, gives each segment the widest
   * one's width: one grid of equal columns and a thumb that steps by its own width. `content`
   * makes each segment as wide as its words and count, which is what a filter row of four
   * health words needs to fit on one line (`clients-v2.html:119-130`), and places the thumb
   * from the chosen segment's own box, read after layout and again when the control resizes.
   * The radio group, the tokens and the look of every variant are the same either way.
   */
  readonly sizing?: 'equal' | 'content' | undefined;
  readonly className?: string | undefined;
}

/**
 * THE TOOLBAR CONTROL HAS ONE HEIGHT, so `size` picks the LABEL and nothing else (SH-A1).
 * The kit's toolbar segmented control is one master at one size - 32x28 buttons in a 36px
 * container - and the two heights this pair used to carry (36 and 28, plus the track's own
 * padding) are what let the control stand taller than the capsules beside it and read as
 * "skewed inside its capsule" (owner-17).
 */
const SIZES = {
  md: 'text-sm',
  sm: 'text-xs',
} as const;

/**
 * The four constructions, each as the token names its own kit master supplies. The
 * geometry differs (height, radius, padding, the gap between items), the MECHANISM does
 * not: one grid of equal columns and one translated thumb, whatever the thumb is made of.
 */
const TRACKS = {
  glass:
    'glass-control rounded-(--radius-button) gap-0 ' +
    'h-(--shell-toolbar-item) p-(--shell-toolbar-group-padding)',
  content: 'rounded-(--home-segment-radius) bg-(--home-segment-track) p-0.5 gap-0',
  contentTinted:
    'rounded-(--segmented-content-radius) bg-(--color-segmented-track) p-0 gap-0 ' +
    'min-h-(--segmented-content-height)',
  ios:
    'rounded-pill bg-(--color-segmented-ios-track) p-(--segmented-ios-inset) ' +
    'gap-(--segmented-ios-gap) min-h-(--segmented-ios-height)',
} as const;

/*
 * `content`'s thumb keeps Home's reasoning verbatim: over glass the thumb is a white
 * surface with `--shadow-raised` under it, which on a flat group header reads as a
 * floating control, so the shadow goes and the track arrives while the thumb stays a
 * surface. `contentTinted`'s is the master's own On fill.
 */
const THUMBS = {
  glass:
    'inset-y-(--shell-toolbar-group-padding) ' +
    'rounded-(--radius-button) bg-(--color-toolbar-segment-selected)',
  content: 'inset-y-0.5 rounded-(--home-segment-radius) bg-surface',
  contentTinted: 'inset-y-0 rounded-(--segmented-content-radius) bg-(--color-segmented-selected)',
  ios: 'inset-y-(--segmented-ios-inset) rounded-pill bg-(--color-segmented-ios-thumb) shadow-(--shadow-segmented-ios-thumb)',
} as const;

/**
 * Where an EQUAL thumb starts: inside the track's padding, because it steps from there by its
 * own width. A CONTENT thumb starts at the padding edge (`left-0`) and is moved by the chosen
 * segment's `offsetLeft`, which is measured from that same edge and already includes the
 * padding.
 */
const THUMB_START = {
  glass: 'left-(--shell-toolbar-group-padding)',
  content: 'left-0.5',
  contentTinted: 'left-0',
  ios: 'left-(--segmented-ios-inset)',
} as const;

/**
 * What the track spends on itself, so the thumb can be an exact item box. `pad` is the
 * track's padding on both sides together and `gap` the space between two items; the thumb
 * is then `(100% - pad - (n-1) * gap) / n` wide and steps by its own width plus one gap.
 * With `gap` at zero that collapses to the old `i * 100%`, which is what the glass and
 * both content tracks do.
 *
 * BOTH ARE CALC TERMS, NOT CALC EXPRESSIONS: a nested `calc()` inside the division makes
 * the whole declaration unparseable to jsdom, which drops it silently, so the unit test
 * that pins the geometry would assert against an empty string and pass forever.
 */
const TRACK_METRICS = {
  glass: { pad: '2 * var(--shell-toolbar-group-padding)', gap: '0px' },
  content: { pad: 'var(--space-1)', gap: '0px' },
  contentTinted: { pad: '0px', gap: '0px' },
  ios: { pad: '2 * var(--segmented-ios-inset)', gap: 'var(--segmented-ios-gap)' },
} as const;

const SEGMENT_SIZES = {
  glass:
    'min-h-(--shell-toolbar-group-item) min-w-(--shell-toolbar-segment-width) ' +
    'px-(--shell-toolbar-segment-inset)',
  /** The content-area duo is ONE height, the kit's 3 Rg, at both `size` values. */
  content: 'min-h-(--home-segment-height) px-2.5 text-xs',
  contentTinted: 'min-h-(--segmented-content-height) px-2 text-2xs',
  ios: 'min-h-[calc(var(--segmented-ios-height)-2*var(--segmented-ios-inset))] px-2 text-2xs',
} as const;

/** The segment's own corner, which is also the shape of the focus ring the input draws. */
const SEGMENT_RADII = {
  glass: 'rounded-(--radius-button)',
  content: 'rounded-(--home-segment-radius)',
  contentTinted: 'rounded-(--segmented-content-radius)',
  ios: 'rounded-pill',
} as const;

export function SegmentedControl<T extends string>({
  value,
  onValueChange,
  segments,
  ariaLabel,
  size = 'md',
  variant = 'glass',
  compactLabels = false,
  sizing = 'equal',
  className,
}: SegmentedControlProps<T>) {
  const name = useId();
  const activeIndex = Math.max(
    segments.findIndex((segment) => segment.value === value),
    0,
  );
  const metrics = TRACK_METRICS[variant];
  const fit = sizing === 'content';

  /* THE CONTENT THUMB IS THE CHOSEN SEGMENT'S OWN BOX, read after layout; a count that changes
     a segment's width moves the thumb with it. The equality check keeps the read from rendering
     again for nothing, and an equal control reads nothing at all. */
  const track = useRef<HTMLFieldSetElement>(null);
  const [box, setBox] = useState<{ readonly left: number; readonly width: number } | null>(null);
  const place = useCallback(() => {
    if (!fit) return;
    const label = track.current?.querySelectorAll('label')[activeIndex];
    if (label === undefined) return;
    const next = { left: label.offsetLeft, width: label.offsetWidth };
    setBox((current) =>
      current?.left === next.left && current.width === next.width ? current : next,
    );
  }, [fit, activeIndex]);
  useLayoutEffect(() => {
    place();
  });
  useLayoutEffect(() => {
    const node = track.current;
    if (!fit || node === null || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(place);
    observer.observe(node);
    return () => observer.disconnect();
  }, [fit, place]);

  return (
    <fieldset
      ref={track}
      className={cx(
        // THE MATERIAL IS THE VARIANT'S, and the default is the toolbar's glass capsule
        // (iOS 26): a sunken gray track next to glass capsules read as a different family
        // up THERE. Inside the content it is the other way round - the sunken track is
        // exactly right and the capsule exactly wrong - which is why the two are a variant
        // and not a class override at the call site (P3 round 2 F4, P5 round 2 F3).
        //
        // NO `m-0` HERE, AND THAT IS THE FIX FOR AUDIT P2-3 (polish wave 4). It was
        // defensive against the browser's own `fieldset { margin-inline: 2px }`, which
        // Tailwind's preflight already zeroes for every element - so it bought nothing
        // and cost this: Tailwind 4 emits `space-y-*` as `:where(.space-y-4 > :not(
        // :last-child))`, which has ZERO specificity, and any margin utility on the
        // child beats it. A `SegmentedControl` in a `space-y-*` stack therefore had no
        // gap under it at all, measured 0px against the intended 16 on the record
        // page's activity feed - a control welded to the top of the card below it.
        //
        // The general rule this belongs to: A PRIMITIVE NEVER SETS ITS OWN MARGIN.
        // Spacing is the composing parent's, and a primitive that pins margin takes
        // the parent's ability to space it away. Gated for this control in
        // `SegmentedControl.test.tsx`; the rule itself is a comment rather than a lint
        // rule, because a NEGATIVE margin inside a primitive is legitimate and common
        // here (`DashboardRow`'s `-mx-2` bleed) and no static rule tells the two apart.
        fit ? 'relative isolate inline-grid' : 'relative isolate grid',
        TRACKS[variant],
        className,
      )}
      style={{
        gridTemplateColumns: fit
          ? `repeat(${segments.length}, auto)`
          : `repeat(${segments.length}, minmax(0, 1fr))`,
      }}
    >
      <legend className="sr-only">{ariaLabel}</legend>

      {fit ? (
        /* Not drawn until the chosen segment has been measured, so it never slides in from the
           wrong place on the first paint. */
        box !== null && (
          <span
            aria-hidden="true"
            className={cx(MOTION.move, 'absolute left-0 -z-10', THUMBS[variant])}
            style={{ width: box.width, transform: `translateX(${String(box.left)}px)` }}
          />
        )
      ) : (
        <span
          aria-hidden="true"
          className={cx(MOTION.move, 'absolute -z-10', THUMBS[variant], THUMB_START[variant])}
          style={{
            width: `calc((100% - ${metrics.pad} - ${segments.length - 1} * ${metrics.gap}) / ${segments.length})`,
            transform: `translateX(calc(${activeIndex} * (100% + ${metrics.gap})))`,
          }}
        />
      )}

      {segments.map((segment) => {
        const active = segment.value === value;
        return (
          <label
            key={segment.value}
            className={cx(
              'relative inline-flex items-center justify-center gap-1.5',
              'font-medium whitespace-nowrap transition-colors select-none',
              SEGMENT_RADII[variant],
              SEGMENT_SIZES[variant],
              variant === 'glass' && SIZES[size],
              // ON THE ACCENT-FILLED SEGMENT THE LABEL IS WHITE IN BOTH APPEARANCES, the
              // way a label on any filled system colour is; the ink token would flip with
              // the theme and put near-black type on blue in the dark.
              variant === 'contentTinted' && active && 'font-semibold',
              active
                ? variant === 'contentTinted'
                  ? 'text-(--color-segmented-selected-ink)'
                  : 'text-ink'
                : 'text-ink-muted hover:text-ink',
            )}
          >
            <input
              type="radio"
              name={name}
              value={segment.value}
              checked={active}
              onChange={() => onValueChange(segment.value)}
              /* `hit-expand-in-place` and not `hit-expand`: this input IS the overlay
                 (`absolute inset-0`), so it must keep its own positioning while its
                 pseudo-element grows the press to the 40px floor - the segments draw at
                 28px in a toolbar (the kit's Segmented Control Buttons) and 24px in both
                 content families (the kit's 3 Rg) and stay that way. */
              className={cx(
                'hit-expand-in-place absolute inset-0 m-0 appearance-none',
                SEGMENT_RADII[variant],
              )}
            />
            {segment.icon !== undefined && (
              <Icon name={segment.icon} size="1.1em" className="pointer-events-none" />
            )}
            <span className={cx('pointer-events-none', compactLabels && 'sr-only md:not-sr-only')}>
              {segment.label}
            </span>
            {segment.count !== undefined && (
              <span
                data-segment-count=""
                className={cx(
                  'pointer-events-none font-normal tabular-nums',
                  /* On the accent-filled segment the label is white, and so is its count:
                     the secondary grey would sink into the blue. */
                  variant === 'contentTinted' && active ? undefined : 'text-ink-muted',
                )}
              >
                {segment.count.toLocaleString('en-US')}
              </span>
            )}
          </label>
        );
      })}
    </fieldset>
  );
}
